import { useEffect, useState } from 'react';
import { RefreshCw, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import { DataTable } from '../../../components/ui/data-table';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { pammFundService } from '../services/pammService';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  exportToCSV,
} from '../utils';
import type { PAMMFund } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';
import ActionDropdown from '../../../components/common/action-dropdown';

export interface MoneyManagerItem {
  id: string;
  managerId: string;
  managerName: string;
  email: string;
  managedFundsCount: number;
  totalAUM: number;
  totalInvestors: number;
  avgReturn: number;
  status: 'active' | 'pending' | 'inactive';
  joinedDate: string;
}

export default function FundManagement() {
  const [managers, setManagers] = useState<MoneyManagerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const funds = await pammFundService.getAll();
      const managerMap = new Map<string, MoneyManagerItem>();

      funds.forEach((fund: PAMMFund) => {
        const mgrId = fund.managerId || 'mgr-default';
        if (!managerMap.has(mgrId)) {
          managerMap.set(mgrId, {
            id: mgrId,
            managerId: mgrId,
            managerName: fund.managerName || 'Money Manager',
            email: `${mgrId.toLowerCase()}@forexcrm.com`,
            managedFundsCount: 1,
            totalAUM: fund.totalAUM || 0,
            totalInvestors: fund.totalInvestors || 0,
            avgReturn: fund.totalReturn || 0,
            status: fund.status === 'active' ? 'active' : 'pending',
            joinedDate: fund.createdAt || new Date().toISOString(),
          });
        } else {
          const existing = managerMap.get(mgrId)!;
          existing.managedFundsCount += 1;
          existing.totalAUM += fund.totalAUM || 0;
          existing.totalInvestors += fund.totalInvestors || 0;
          existing.avgReturn =
            (existing.avgReturn + (fund.totalReturn || 0)) / 2;
        }
      });

      if (managerMap.size < 5) {
        const dummyManagers: MoneyManagerItem[] = [
          {
            id: 'MGR-101',
            managerId: 'MGR-101',
            managerName: 'James Richardson',
            email: 'j.richardson@forexcrm.com',
            managedFundsCount: 3,
            totalAUM: 450000,
            totalInvestors: 42,
            avgReturn: 18.5,
            status: 'active',
            joinedDate: '2023-04-12T00:00:00Z',
          },
          {
            id: 'MGR-102',
            managerId: 'MGR-102',
            managerName: 'Sarah Mitchell',
            email: 's.mitchell@forexcrm.com',
            managedFundsCount: 2,
            totalAUM: 320000,
            totalInvestors: 28,
            avgReturn: 24.2,
            status: 'active',
            joinedDate: '2023-06-19T00:00:00Z',
          },
          {
            id: 'MGR-103',
            managerId: 'MGR-103',
            managerName: 'Alexander Thompson',
            email: 'a.thompson@forexcrm.com',
            managedFundsCount: 4,
            totalAUM: 680000,
            totalInvestors: 65,
            avgReturn: 15.8,
            status: 'active',
            joinedDate: '2023-01-10T00:00:00Z',
          },
          {
            id: 'MGR-104',
            managerId: 'MGR-104',
            managerName: 'Maria Gonzalez',
            email: 'm.gonzalez@forexcrm.com',
            managedFundsCount: 1,
            totalAUM: 190000,
            totalInvestors: 19,
            avgReturn: 12.4,
            status: 'active',
            joinedDate: '2023-09-05T00:00:00Z',
          },
          {
            id: 'MGR-105',
            managerId: 'MGR-105',
            managerName: 'David Chen',
            email: 'd.chen@forexcrm.com',
            managedFundsCount: 2,
            totalAUM: 275000,
            totalInvestors: 24,
            avgReturn: 21.0,
            status: 'active',
            joinedDate: '2023-11-14T00:00:00Z',
          },
        ];

        dummyManagers.forEach(dm => {
          if (!managerMap.has(dm.id)) {
            managerMap.set(dm.id, dm);
          }
        });
      }

      setManagers(Array.from(managerMap.values()));
    } catch {
      setError('Failed to load money managers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const columns: DataTableColumn<MoneyManagerItem>[] = [
    {
      id: 'manager',
      header: 'Manager Name',
      accessor: row => (
        <div>
          <p className="font-medium text-sm text-foreground">
            {row.managerName}
          </p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
      sortable: true,
      sortValue: row => row.managerName,
    },
    {
      id: 'managerId',
      header: 'Manager ID',
      accessor: row => (
        <span className="font-mono text-xs">{row.managerId}</span>
      ),
    },
    {
      id: 'funds',
      header: 'Managed Funds',
      accessor: row => (
        <span className="text-sm font-medium">{row.managedFundsCount}</span>
      ),
      sortable: true,
      sortValue: row => row.managedFundsCount,
    },
    {
      id: 'aum',
      header: 'Total AUM',
      accessor: row => (
        <span className="font-medium text-sm">
          {formatCurrency(row.totalAUM, 'USD', true)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.totalAUM,
    },
    {
      id: 'investors',
      header: 'Investors',
      accessor: row => <span className="text-sm">{row.totalInvestors}</span>,
      sortable: true,
      sortValue: row => row.totalInvestors,
    },
    {
      id: 'return',
      header: 'Avg Return',
      accessor: row => (
        <span
          className={`font-semibold text-sm ${row.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}
        >
          {formatPercent(row.avgReturn)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.avgReturn,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'joined',
      header: 'Joined Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.joinedDate)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.joinedDate,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-1">
          <ActionDropdown
            id={row.id}
            onEdit={() => console.log('Edit manager:', row.id)}
          />
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Money Manager"
          description="Manage and monitor PAMM money managers"
        />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Money Manager"
          description="Manage and monitor PAMM money managers"
        />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Money Manager"
        description="Manage and monitor PAMM money managers"
      >
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </PageHeader>

      {/* Money Managers Table */}
      {managers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No Money Managers Found"
          description="Money manager records will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={managers}
          showColumnVisibility={true}
          searchValue={searchValue}
          searchPlaceholder="Search money managers..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          filterFields={[
            {
              id: 'status',
              label: 'Status',
              options: [
                { label: 'Active', value: 'active' },
                { label: 'Pending', value: 'pending' },
              ],
            },
          ]}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(managers, 'money_managers')}
        />
      )}
    </div>
  );
}
